import React, { useState, useEffect } from 'react';
import Spline from '@splinetool/react-spline';
import { 
  Shield, 
  Globe, 
  Eye, 
  Bug, 
  Code, 
  Zap, 
  Radio, 
  Cog, 
  Search, 
  Smartphone, 
  Network, 
  Users, 
  Cpu, 
  Clock, 
  Award,
  Menu,
  X,
  ChevronDown,
  ArrowRight,
  Languages
} from 'lucide-react';
import { mockServices, mockTrainings, mockWhyChooseUs, mockCompanyInfo, translations } from '../mock';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { RippleButton, RippleLink } from './RippleButton';
import { TrainingModal } from './TrainingModal';

const iconMap = {
  Shield, Globe, Eye, Bug, Code, Zap, Radio, Cog, Search, Smartphone, Network, Users, Cpu, Clock, Award
};

const Header = ({ activeSection, setActiveSection, language, setLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  const t = translations[language];

  const menuItems = [
    { id: 'why-us', label: t.whyUs },
    { id: 'services', label: t.services },
    { id: 'trainings', label: t.trainings },
    { id: 'contact', label: t.contact }
  ];

  const languages = [
    { code: 'uz', name: "O'zbekcha", flag: '🇺🇿' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-bg-primary/90 backdrop-blur-xl border-b border-border-medium shadow-lg">
      <div className="section-container">
        <div className="flex items-center justify-between py-1 md:py-2">
          
          {/* Modern Logo */}
          <div 
            className="cursor-pointer"
            onClick={() => scrollToSection('home')}
          >
            <div className="h-20 md:h-24 transition-all duration-300 hover:scale-105 scale-125">
              <img 
                src="/kiberup-logo.png" 
                alt="KiberUP Logo" 
                className="h-full w-auto object-contain"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`nav-link relative ${
                  activeSection === item.id 
                    ? 'text-brand-teal-light' 
                    : 'text-text-tertiary hover:text-text-primary'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Internship Apply Button */}
            <RippleLink
              href="https://forms.gle/aBEp2q7TCeHa4srW7"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 bg-gradient-to-r from-brand-teal to-brand-teal-light text-white font-semibold rounded-lg hover:shadow-glow-md transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center justify-center"
            >
              {t.internshipApply}
            </RippleLink>
            
            {/* Enhanced Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="flex items-center gap-3 px-4 py-2 glass-card rounded-lg hover:border-brand-teal transition-all duration-300 group"
              >
                <span className="text-xl">
                  {languages.find(lang => lang.code === language)?.flag}
                </span>
                <span className="text-body-small font-medium text-text-secondary group-hover:text-brand-teal-light">
                  {languages.find(lang => lang.code === language)?.name}
                </span>
                <ChevronDown className={`w-4 h-4 text-text-tertiary group-hover:text-brand-teal-light transition-all duration-300 ${isLanguageMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLanguageMenuOpen && (
                <div className="absolute top-full right-0 mt-2 glass-card rounded-xl min-w-[200px] z-50 overflow-hidden shadow-glow-lg animate-scale-in">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLanguageMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all duration-200 ${
                        language === lang.code 
                          ? 'text-brand-teal-light bg-brand-teal/20 border-l-4 border-brand-teal-light' 
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/50'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <div>
                        <div className="font-medium">{lang.name}</div>
                        <div className="text-caption text-text-tertiary">{lang.code.toUpperCase()}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-text-tertiary hover:text-brand-teal-light transition-all duration-300 hover:bg-bg-tertiary rounded-lg"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden glass-card border-t border-border-medium animate-slide-in-left">
          <div className="section-container py-6 space-y-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  scrollToSection(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={`block w-full text-left py-3 px-4 rounded-lg transition-all duration-300 ${
                  activeSection === item.id 
                    ? 'text-brand-teal-light bg-brand-teal/20 border-l-4 border-brand-teal-light' 
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Internship Apply Button - Mobile */}
            <RippleLink
              href="https://forms.gle/aBEp2q7TCeHa4srW7"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-full py-3 px-4 bg-gradient-to-r from-brand-teal to-brand-teal-light text-white font-semibold rounded-lg hover:shadow-glow-md transition-all duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.internshipApply}
            </RippleLink>
            
            {/* Mobile Language Selector */}
            <div className="pt-4 border-t border-border-medium">
              <div className="grid grid-cols-2 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                      language === lang.code 
                        ? 'text-brand-teal-light bg-brand-teal/20 border border-brand-teal' 
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary border border-transparent'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-body-small font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const HeroSection = ({ language }) => {
  const t = translations[language];
  const [leftRef, leftVisible] = useIntersectionObserver();
  const [rightRef, rightVisible] = useIntersectionObserver();
  
  return (
    <section id="home" className="min-h-screen flex items-center relative overflow-hidden pt-28 md:pt-32">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-bg-darker via-bg-primary to-bg-secondary opacity-50"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,167,157,0.1),transparent_50%)]"></div>
      
      <div className="max-w-7xl mx-auto px-4 lg:px-16 py-20 grid lg:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left Content */}
        <div ref={leftRef} className={`space-y-8 scroll-reveal-left ${leftVisible ? 'visible' : ''}`}>
          <div className="space-y-4">
            <h1 className="display-hero gradient-text leading-tight">
              {t.heroTitle.split(' ').slice(0, 2).join(' ')}
              <br />
              <span className="text-brand-teal-light">{t.heroTitle.split(' ').slice(2, 4).join(' ')}</span>
              <br />
              {t.heroTitle.split(' ').slice(4).join(' ')}
            </h1>
            <p className="body-large text-text-secondary max-w-lg leading-relaxed">
              {t.heroDescription}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <RippleButton 
              onClick={() => {
                const element = document.getElementById('services');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary"
            >
              {t.discoverServices}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </RippleButton>
            <RippleButton 
              onClick={() => {
                const element = document.getElementById('contact');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-secondary"
            >
              {t.getInTouch}
            </RippleButton>
          </div>

          <div className="grid grid-cols-3 gap-8 pt-8">
            <div className="text-center group cursor-pointer">
              <div className="text-4xl font-bold text-brand-teal-light group-hover:scale-110 transition-transform duration-300">100+</div>
              <div className="body-small text-text-tertiary group-hover:text-text-secondary transition-colors">{t.successfulProjects}</div>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="text-4xl font-bold text-brand-teal-light group-hover:scale-110 transition-transform duration-300">10+</div>
              <div className="body-small text-text-tertiary group-hover:text-text-secondary transition-colors">{t.yearsExperience}</div>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="text-4xl font-bold text-brand-teal-light group-hover:scale-110 transition-transform duration-300">7/24</div>
              <div className="body-small text-text-tertiary group-hover:text-text-secondary transition-colors">{t.support}</div>
            </div>
          </div>
        </div>

        {/* Right Content - 3D Animation */}
        <div ref={rightRef} className={`flex justify-center lg:justify-end scroll-reveal-right ${rightVisible ? 'visible' : ''}`}>
          <div className="w-full max-w-lg h-96 lg:h-[500px] relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/20 to-brand-teal-light/10 rounded-3xl blur-3xl group-hover:blur-2xl transition-all duration-500"></div>
            <Spline 
              scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
              className="w-full h-full relative z-10"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyChooseUsSection = ({ language }) => {
  const t = translations[language];
  const [titleRef, titleVisible] = useIntersectionObserver();
  const [cardsRef, cardsVisible] = useIntersectionObserver();
  
  return (
    <section id="why-us" className="py-20 bg-bg-secondary/50 relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(0,167,157,0.08),transparent_50%)]"></div>
      
      <div className="max-w-7xl mx-auto px-4 lg:px-16 relative z-10">
        <div ref={titleRef} className={`text-center mb-16 scroll-reveal ${titleVisible ? 'visible' : ''}`}>
          <h2 className="display-large mb-6 gradient-text">{t.whyChooseUsTitle}</h2>
          <p className="body-large text-text-secondary max-w-6xl mx-auto leading-relaxed">
            {t.whyChooseUsDescription}
          </p>
        </div>

        <div ref={cardsRef} className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 scroll-reveal-scale ${cardsVisible ? 'visible' : ''}`}>
          {mockWhyChooseUs[language].map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <div 
                key={item.id} 
                className="glass-card p-6 group cursor-pointer"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-brand-teal/30 to-brand-teal-light/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:shadow-glow-md transition-all duration-300">
                  <IconComponent className="w-7 h-7 text-brand-teal-light" />
                </div>
                <h3 className="heading-3 mb-3 group-hover:text-brand-teal-light transition-colors">{item.title}</h3>
                <p className="body-small text-text-secondary leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ServicesSection = ({ language }) => {
  const [selectedService, setSelectedService] = useState(mockServices[language][0]);
  const t = translations[language];
  const [titleRef, titleVisible] = useIntersectionObserver();
  const [contentRef, contentVisible] = useIntersectionObserver();

  // Update selected service when language changes
  React.useEffect(() => {
    setSelectedService(mockServices[language][0]);
  }, [language]);

  return (
    <section id="services" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 lg:px-16">
        <div ref={titleRef} className={`text-center mb-16 scroll-reveal ${titleVisible ? 'visible' : ''}`}>
          <h2 className="display-large mb-6 gradient-text">{t.servicesTitle}</h2>
          <p className="body-large text-text-secondary max-w-3xl mx-auto leading-relaxed">
            {t.servicesDescription}
          </p>
        </div>

        <div ref={contentRef} className={`grid lg:grid-cols-3 gap-8 scroll-reveal ${contentVisible ? 'visible' : ''}`}>
          {/* Service List */}
          <div className="lg:col-span-1 space-y-3">
            {mockServices[language].map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <RippleButton
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                    selectedService.id === service.id
                      ? 'border-brand-teal bg-brand-teal/20 shadow-glow-md'
                      : 'border-border-light bg-bg-tertiary/50 hover:border-brand-teal/50 hover:bg-bg-tertiary'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <IconComponent className={`w-5 h-5 transition-colors ${
                      selectedService.id === service.id ? 'text-brand-teal-light' : 'text-text-tertiary'
                    }`} />
                    <span className={`font-medium transition-colors ${
                      selectedService.id === service.id ? 'text-brand-teal-light' : 'text-text-primary'
                    }`}>
                      {service.title}
                    </span>
                  </div>
                </RippleButton>
              );
            })}
          </div>

          {/* Service Detail */}
          <div className="lg:col-span-2 glass-card p-8 rounded-2xl">
            <div className="flex items-center gap-4 mb-6">
              {(() => {
                const IconComponent = iconMap[selectedService.icon];
                return (
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-teal/30 to-brand-teal-light/20 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-brand-teal-light" />
                  </div>
                );
              })()}
              <h3 className="heading-2 gradient-text">{selectedService.title}</h3>
            </div>
            
            <p className="body-medium text-text-secondary mb-8 leading-relaxed">
              {selectedService.description}
            </p>

            <div className="space-y-4">
              <h4 className="heading-3 text-brand-teal-light">{t.features}</h4>
              <ul className="space-y-3">
                {selectedService.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 body-small group">
                    <div className="w-2 h-2 bg-brand-teal-light rounded-full mt-2 group-hover:scale-150 transition-transform"></div>
                    <span className="text-text-secondary group-hover:text-text-primary transition-colors">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <RippleLink
              href="https://forms.gle/JJM6HVQVfcKRWZZs7" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary mt-8 inline-flex"
            >
              {t.getDetailedInfo}
              <ArrowRight className="w-5 h-5" />
            </RippleLink>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrainingsSection = ({ language }) => {
  const t = translations[language];
  const [titleRef, titleVisible] = useIntersectionObserver();
  const [cardsRef, cardsVisible] = useIntersectionObserver();
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (training) => {
    setSelectedTraining(training);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedTraining(null), 300);
  };

  const viewDetailsText = {
    tr: 'Detayları Gör',
    uz: 'Batafsil',
    ru: 'Подробнее',
    en: 'View Details'
  };
  
  return (
    <>
      <section id="trainings" className="py-20 bg-bg-secondary/50 relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(0,167,157,0.08),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 lg:px-16 relative z-10">
          <div ref={titleRef} className={`text-center mb-16 scroll-reveal ${titleVisible ? 'visible' : ''}`}>
            <h2 className="display-large mb-6 gradient-text">{t.trainingsTitle}</h2>
            <p className="body-large text-text-secondary max-w-3xl mx-auto leading-relaxed">
              {t.trainingsDescription}
            </p>
          </div>

          <div ref={cardsRef} className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 scroll-reveal-scale ${cardsVisible ? 'visible' : ''}`}>
          {mockTrainings[language].map((training, index) => {
            const IconComponent = iconMap[training.icon];
            return (
              <div 
                key={training.id} 
                className="glass-card p-6 group cursor-pointer flex flex-col hover:shadow-[0_0_12px_rgba(0,224,255,0.2)] transition-all duration-300"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-teal/30 to-brand-teal-light/20 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:shadow-glow-md transition-all duration-300">
                    <IconComponent className="w-6 h-6 text-brand-teal-light" />
                  </div>
                  <div className="text-xs px-3 py-1.5 bg-brand-teal/20 text-brand-teal-light font-medium rounded-full border border-brand-teal/30">
                    {training.level}
                  </div>
                </div>

                <h3 className="heading-3 mb-3 group-hover:text-brand-teal-light transition-colors line-clamp-2">
                  {training.title}
                </h3>
                <p className="body-small text-text-secondary mb-4 leading-relaxed flex-grow line-clamp-3">
                  {training.description}
                </p>

                <div className="flex items-center justify-between mb-4 text-sm pt-4 border-t border-border-light">
                  <span className="text-text-tertiary flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {training.duration}
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  <RippleButton
                    onClick={() => openModal(training)}
                    className="btn-secondary w-full text-center justify-center"
                  >
                    {viewDetailsText[language] || viewDetailsText.en}
                  </RippleButton>
                  <RippleLink
                    href="https://forms.gle/c1SduPx1vdLyDp2U7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full text-center justify-center text-sm"
                  >
                    {t.registerTraining}
                  </RippleLink>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Training Note */}
        <div className="mt-16 glass-card p-8 rounded-2xl animate-fade-in">
          <div className="max-w-4xl mx-auto">
            <p className="body-medium text-text-secondary leading-relaxed">
              <strong className="text-brand-teal-light">{language === 'tr' ? 'Not:' : language === 'uz' ? 'Eslatma:' : language === 'ru' ? 'Примечание:' : 'Note:'}</strong> {t.trainingNote}
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Training Modal */}
    <TrainingModal 
      isOpen={isModalOpen}
      onClose={closeModal}
      training={selectedTraining}
      language={language}
    />
    </>
  );
};

const ContactSection = ({ language }) => {
  const t = translations[language];
  const [titleRef, titleVisible] = useIntersectionObserver();
  const [cardsRef, cardsVisible] = useIntersectionObserver();
  
  return (
    <section id="contact" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 lg:px-16">
        <div ref={titleRef} className={`text-center mb-16 scroll-reveal ${titleVisible ? 'visible' : ''}`}>
          <h2 className="display-large mb-6 gradient-text">{t.contactTitle}</h2>
          <div className="max-w-5xl mx-auto">
            <p className="body-large text-text-secondary leading-relaxed">
              {t.contactDescription}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div ref={cardsRef} className={`grid md:grid-cols-2 gap-8 scroll-reveal-scale ${cardsVisible ? 'visible' : ''}`}>
            <div className="glass-card p-8 rounded-2xl group hover:border-brand-teal transition-all duration-300">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-teal/30 to-brand-teal-light/20 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:shadow-glow-md transition-all duration-300">
                  <Shield className="w-8 h-8 text-brand-teal-light" />
                </div>
                <div>
                  <div className="font-semibold text-xl text-brand-teal-light mb-2">{t.email}</div>
                  <div className="text-text-secondary text-lg group-hover:text-text-primary transition-colors">{mockCompanyInfo.email}</div>
                </div>
              </div>
            </div>
            
            <div className="glass-card p-8 rounded-2xl group hover:border-brand-teal transition-all duration-300">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-teal/30 to-brand-teal-light/20 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:shadow-glow-md transition-all duration-300">
                  <Globe className="w-8 h-8 text-brand-teal-light" />
                </div>
                <div>
                  <div className="font-semibold text-xl text-brand-teal-light mb-2">{t.address}</div>
                  <div className="text-text-secondary text-lg group-hover:text-text-primary transition-colors">{mockCompanyInfo.address[language] || mockCompanyInfo.address.uz}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Modal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="glass-card max-w-lg w-full p-8 relative rounded-2xl shadow-glow-xl animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-tertiary hover:text-brand-teal-light transition-all duration-300 hover:rotate-90 p-2 rounded-lg hover:bg-bg-tertiary"
        >
          <X className="w-6 h-6" />
        </button>
        <h3 className="text-2xl font-bold gradient-text mb-4">{title}</h3>
        <p className="text-text-secondary leading-relaxed">{content}</p>
      </div>
    </div>
  );
};

const Footer = ({ language }) => {
  const t = translations[language];
  const [modalOpen, setModalOpen] = useState(null);

  const modalContents = {
    tr: {
      privacy: {
        title: "🔒 Gizlilik Politikası",
        content: "Kullanıcı bilgilerinizin güvenliği bizim için önceliklidir.\n\n• UzCyber, hizmetlerimizi geliştirmek, kullanıcı deneyimini iyileştirmek ve güvenliği sağlamak amacıyla bazı kişisel verileri işler.\n\n• Toplanan veriler yalnızca belirli ve meşru amaçlar doğrultusunda kullanılmakta olup, hiçbir şekilde üçüncü kişilerle ticari amaçla paylaşılmaz.\n\n• Verileriniz yalnızca hizmet sağlayıcılarımız (örneğin barındırma, güvenlik, e-posta altyapısı) ile sınırlı ve sözleşmeye dayalı olarak paylaşılabilir.\n\n• Sitemiz, deneyimi kişiselleştirmek ve istatistiksel analizler yapmak amacıyla çerez (cookie) kullanabilir. Dilerseniz tarayıcınızın ayarlarından çerezleri devre dışı bırakabilirsiniz.\n\n• Tüm kişisel veriler, ilgili yasal mevzuata (KVKK ve GDPR) uygun olarak güvenli biçimde saklanır ve gerektiğinde silinir veya anonim hale getirilir.\n\nHerhangi bir sorunuz olduğunda veya kişisel verilerinizle ilgili işlem talep etmek isterseniz bizimle şu adresten iletişime geçebilirsiniz: info@uzcyber.com"
      },
      terms: {
        title: "📑 Kullanım Şartları", 
        content: "Bu siteyi ziyaret eden tüm kullanıcılar, aşağıdaki şartları kabul etmiş sayılır:\n\n• UzCyber tarafından sunulan tüm içerikler yalnızca bilgilendirme ve eğitim amacıyla paylaşılmaktadır.\n\n• Burada yer alan hiçbir teknik bilgi, yöntem veya araç, yasal olmayan siber güvenlik faaliyetlerinde kullanılmamalıdır.\n\n• Site içeriğinin izinsiz kopyalanması, paylaşılması veya ticari amaçla kullanılması yasaktır.\n\n• Kullanıcılar, siteyi yalnızca yasal çerçevede ve etik kurallar doğrultusunda kullanmayı kabul eder.\n\n• UzCyber, içeriklerde yer alan hatalar, teknik kesintiler veya üçüncü taraf bağlantılardan doğabilecek zararlardan sorumlu tutulamaz.\n\n• Site içeriği ve politikalar önceden haber verilmeksizin güncellenebilir.\n\n• Sitemizi kullanmaya devam eden her kullanıcı, bu şartları kabul etmiş sayılır.\n\nDetaylı kullanım şartlarını okumak için bizimle iletişime geçebilirsiniz: info@uzcyber.com"
      },
      about: {
        title: "ℹ️ Hakkımızda",
        content: "UzCyber, güçlü teknik kadrosu ve yenilikçi yaklaşımıyla işletmelerin ve bireylerin dijital dünyadaki güvenliğini sağlamayı amaçlayan bir siber güvenlik firmasıdır.\n\nAmacımız; kurumların, girişimlerin ve kullanıcıların dijital varlıklarını koruyarak güvenli, sürdürülebilir ve bilinçli bir siber ekosistem oluşturmak.\n\nUzCyber ekibi; tehdit istihbaratı, sızma testleri, güvenlik izleme, olay müdahalesi, eğitim ve danışmanlık alanlarında uzmanlaşmış mühendis ve araştırmacılardan oluşur.\n\nYalnızca savunmaya değil, aynı zamanda farkındalık ve bilgi paylaşımına da önem veririz — çünkü güçlü bir siber dünya, bilgiyi paylaşarak büyür.\n\nUluslararası standartlara uygun çözümlerimizle, bireylerden kurumlara kadar herkes için güvenli dijital dönüşüm sağlamayı hedefliyoruz.\n\nUzCyber, teknolojiyle güveni birleştirir."
      }
    },
    uz: {
      privacy: {
        title: "🔒 Maxfiylik Siyosati",
        content: "Foydalanuvchi ma'lumotlaringizning xavfsizligi biz uchun ustuvordir. Ma'lumotlaringizni uchinchi shaxslar bilan baham ko'rmaymiz, faqat xizmat sifatimizni oshirish uchun ishlatamiz."
      },
      terms: {
        title: "📑 Foydalanish Shartlari", 
        content: "Ushbu saytga tashrif buyurgan barcha foydalanuvchilar quyidagi shartlarni qabul qilgan hisoblanadi:\n\n• UzCyber tomonidan taqdim etilgan barcha maʼlumotlar faqat axborot va taʼlim maqsadlarida ulashiladi.\n\n• Bu yerda keltirilgan hech qanday texnik maʼlumot, usul yoki vosita noqonuniy kiberxavfsizlik faoliyatlarida qoʻllanilmasligi kerak.\n\n• Sayt mazmunining ruxsatsiz nusxalanishi, tarqatilishi yoki tijorat maqsadlarida ishlatilishi taqiqlanadi.\n\n• Foydalanuvchilar saytni faqat qonuniy doirada va axloqiy qoidalarga rioya qilgan holda foydalanishni qabul qiladi.\n\n• UzCyber kontentdagi xatoliklar, texnik uzilishlar yoki uchinchi tomon havolalaridan kelib chiqadigan zararlarga javobgar emas.\n\n• Sayt mazmuni va siyosatlari oldindan xabardor qilinmasdan yangilanishi mumkin.\n\n• Saytdan foydalanishni davom ettirgan har bir foydalanuvchi ushbu shartlarni qabul qilgan hisoblanadi.\n\nBatafsil foydalanish shartlarini oʻqish uchun biz bilan bogʻlaning: info@uzcyber.com"
      },
      about: {
        title: "ℹ️ Biz Haqimizda",
        content: "UzCyber — kuchli texnik jamoasi va innovatsion yondashuvi bilan korxonalar va foydalanuvchilarning raqamli dunyodagi xavfsizligini ta'minlashga qaratilgan kiberxavfsizlik kompaniyasidir.\n\nBizning maqsadimiz — tashkilotlar, startaplar va foydalanuvchilarning raqamli aktivlarini himoya qilib, xavfsiz, barqaror va ongli kiber ekotizim yaratishdir.\n\nUzCyber jamoasi tahdid razvedkasi, tarmoqqa kirish testlari, xavfsizlik monitoringi, hodisalarga javob berish, ta'lim va maslahat yo'nalishlarida tajribali muhandislar va tadqiqotchilardan iborat.\n\nBiz faqat himoyaga emas, balki xabardorlik va bilim almashishga ham e'tibor qaratamiz — chunki kuchli kiber dunyo bilim orqali rivojlanadi.\n\nXalqaro standartlarga mos yechimlarimiz bilan, biz har bir inson va tashkilot uchun xavfsiz raqamli transformatsiyani ta'minlashni maqsad qilganmiz.\n\nUzCyber — texnologiya va ishonchni birlashtiradi."
      }
    },
    ru: {
      privacy: {
        title: "🔒 Политика конфиденциальности",
        content: "Безопасность информации пользователей является приоритетом для нас. Мы не делимся вашими данными с третьими лицами, используем их только для улучшения качества наших услуг."
      },
      terms: {
        title: "📑 Условия использования", 
        content: "Все пользователи, посещающие данный сайт, считаются принявшими следующие условия:\n\n• Весь контент, предоставляемый UzCyber, предназначен исключительно для информационных и образовательных целей.\n\n• Любая техническая информация, метод или инструмент не должны использоваться для незаконной кибердеятельности.\n\n• Копирование, распространение или коммерческое использование материалов сайта без разрешения запрещено.\n\n• Пользователи обязуются использовать сайт только в рамках закона и в соответствии с этическими принципами.\n\n• UzCyber не несёт ответственности за ошибки, технические сбои или ущерб, вызванный сторонними ссылками и ресурсами.\n\n• Содержание и политика сайта могут быть изменены без предварительного уведомления.\n\n• Продолжая использование сайта, каждый пользователь считается принявшим данные условия.\n\nДля получения подробной информации свяжитесь с нами: info@uzcyber.com"
      },
      about: {
        title: "ℹ️ О нас",
        content: "UzCyber — компания в сфере кибербезопасности, цель которой — обеспечить цифровую безопасность бизнеса и частных лиц благодаря сильной технической команде и инновационному подходу.\n\nНаша миссия — создать безопасную, устойчивую и осознанную киберэкосистему, защищая цифровые активы организаций, предприятий и пользователей.\n\nКоманда UzCyber состоит из инженеров и исследователей, специализирующихся на киберразведке, пентестах, мониторинге безопасности, реагировании на инциденты, обучении и консалтинге.\n\nМы ценим не только защиту, но и осведомлённость, а также обмен знаниями — ведь сильный кибермир растёт через сотрудничество и образование.\n\nПредлагая решения, соответствующие международным стандартам, мы стремимся обеспечить безопасную цифровую трансформацию для всех — от частных лиц до крупных компаний.\n\nUzCyber объединяет технологии и доверие."
      }
    },
    en: {
      privacy: {
        title: "🔒 Privacy Policy",
        content: "The security of user information is our priority. We do not share your data with third parties, we only use it to improve the quality of our services."
      },
      terms: {
        title: "📑 Terms of Use", 
        content: "All users who visit this website are deemed to have accepted the following terms:\n\n• All content provided by UzCyber is shared for informational and educational purposes only.\n\n• No technical information, method, or tool provided here may be used for unlawful cybersecurity activities.\n\n• It is prohibited to copy, distribute, or use any content on this site for commercial purposes without permission.\n\n• Users agree to use the website only within the legal framework and in accordance with ethical principles.\n\n• UzCyber is not responsible for any errors, interruptions, or damages caused by third-party links or external content.\n\n• The website content and policies may be updated without prior notice.\n\n• By continuing to use this site, each user is considered to have accepted these terms.\n\nFor detailed terms of use, please contact us at: info@uzcyber.com"
      },
      about: {
        title: "ℹ️ About Us",
        content: "UzCyber is a cybersecurity company that aims to ensure the digital safety of businesses and individuals through its strong technical team and innovative approach.\n\nOur mission is to create a secure, sustainable, and conscious cyber ecosystem by protecting the digital assets of organizations, enterprises, and users.\n\nThe UzCyber team consists of engineers and researchers specialized in threat intelligence, penetration testing, security monitoring, incident response, training, and consulting.\n\nWe value not only defense but also awareness and knowledge sharing — because a strong cyber world grows through collaboration and information exchange.\n\nWith solutions that meet international standards, we strive to enable a safe digital transformation for everyone — from individuals to large organizations.\n\nUzCyber combines technology with trust."
      }
    }
  };
  
  return (
    <footer className="bg-bg-darker border-t border-border-medium py-16 relative">
      <div className="absolute inset-0 bg-gradient-to-t from-bg-darker via-bg-primary/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 lg:px-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div className="space-y-6">
            <div className="cursor-pointer group">
              <div className="h-32 md:h-40 transition-all duration-300 group-hover:scale-105">
                <img 
                  src="/kiberup-logo.png" 
                  alt="KiberUP Logo" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>
            <p className="body-medium text-text-secondary leading-relaxed">
              {t.footerDescription}
            </p>
            <div className="space-y-3">
              <div className="text-text-tertiary hover:text-text-primary transition-colors group">
                <strong className="text-brand-teal-light">{t.email}:</strong> 
                <span className="ml-2">{mockCompanyInfo.email}</span>
              </div>
              <div className="text-text-tertiary hover:text-text-primary transition-colors group">
                <strong className="text-brand-teal-light">{t.address}:</strong> 
                <span className="ml-2">{mockCompanyInfo.address[language] || mockCompanyInfo.address.uz}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-6 text-brand-teal-light">{t.ourServices}</h4>
            <ul className="space-y-3 text-text-tertiary">
              {t.footerServices?.map((service, index) => (
                <li key={index} className="hover:text-brand-teal-light transition-all duration-200 cursor-pointer hover:translate-x-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-teal rounded-full"></span>
                  {service}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-6 text-brand-teal-light">{t.ourTrainings}</h4>
            <ul className="space-y-3 text-text-tertiary">
              {t.footerTrainings?.map((training, index) => (
                <li key={index} className="hover:text-brand-teal-light transition-all duration-200 cursor-pointer hover:translate-x-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-teal rounded-full"></span>
                  {training}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border-medium pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-text-tertiary">
              © 2025 KiberUP.com {t.rightsReserved}
            </p>
            <div className="flex items-center gap-8 text-text-tertiary">
              <button 
                onClick={() => setModalOpen('privacy')}
                className="hover:text-brand-teal-light transition-colors"
              >
                {t.privacyPolicy}
              </button>
              <button 
                onClick={() => setModalOpen('terms')}
                className="hover:text-brand-teal-light transition-colors"
              >
                {t.termsOfUse}
              </button>
              <button 
                onClick={() => setModalOpen('about')}
                className="hover:text-brand-teal-light transition-colors"
              >
                {t.aboutUs}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={modalOpen !== null}
        onClose={() => setModalOpen(null)}
        title={modalOpen ? modalContents[language][modalOpen].title : ''}
        content={modalOpen ? modalContents[language][modalOpen].content : ''}
      />
    </footer>
  );
};

const HomePage = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [language, setLanguage] = useState('uz');

  // Update document title based on language
  useEffect(() => {
    const t = translations[language];
    document.title = `${t.siteName} - ${t.siteSlogan}`;
  }, [language]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'why-us', 'services', 'trainings', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="dark-container">
      <Header 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        language={language}
        setLanguage={setLanguage}
      />
      <main>
        <HeroSection language={language} />
        <WhyChooseUsSection language={language} />
        <ServicesSection language={language} />
        <TrainingsSection language={language} />
        <ContactSection language={language} />
      </main>
      <Footer language={language} />
    </div>
  );
};

export default HomePage;